import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatMetric } from '../panelConfig';

const hasRightAxis = (series) => series.some((s) => s.yAxisId === 'right');

// Compacto para los ticks del eje (120K, 1.2M); el $ y el detalle van al tooltip.
function compact(v) {
  if (v == null) return '';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
  return `${Math.round(v)}`;
}

function axisTick(format) {
  return (v) => {
    if (v == null) return '';
    if (format === 'percent') return `${Math.round(v * 100)}%`;
    if (format === 'ratio') return `${v}x`;
    return compact(v);
  };
}

const AXIS = { fontSize: 11, fill: '#9ca3af' };

/**
 * Gráfico de líneas con relleno de área en degradado (look CoinMarketCap).
 * Cada serie: { dataKey, label, color, yAxisId?, format?, dashed? }. `dashed`
 * (p.ej. una meta) se dibuja como línea punteada sin relleno. El formato por
 * serie alimenta el tooltip; el eje usa el formato de su primera serie.
 */
export default function TimeSeriesChart({ title, data, series, showLegend = true, card = true, height = 260 }) {
  const rightAxis = hasRightAxis(series);
  const leftFmt = (series.find((s) => (s.yAxisId || 'left') === 'left') || {}).format || 'number';
  const rightFmt = (series.find((s) => s.yAxisId === 'right') || {}).format || 'number';
  const formatByName = Object.fromEntries(series.map((s) => [s.label, s.format || 'number']));

  const chart = (
    <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <defs>
            {series.filter((s) => !s.dashed).map((s) => (
              <linearGradient key={s.dataKey} id={`grad-${s.dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid vertical={false} stroke="#eef0f2" />
          <XAxis dataKey="date" tickLine={false} axisLine={false} tick={AXIS} minTickGap={28} />
          <YAxis yAxisId="left" tickLine={false} axisLine={false} tick={AXIS} width={52}
                 tickFormatter={axisTick(leftFmt)} />
          {rightAxis && (
            <YAxis yAxisId="right" orientation="right" tickLine={false} axisLine={false} tick={AXIS}
                   width={52} tickFormatter={axisTick(rightFmt)} />
          )}
          <Tooltip
            formatter={(value, name) => [formatMetric(value, formatByName[name] || 'number'), name]}
            contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
          />
          {showLegend && <Legend />}
          {series.map((s) =>
            s.dashed ? (
              <Line
                key={s.dataKey}
                yAxisId={s.yAxisId || 'left'}
                type="monotone"
                dataKey={s.dataKey}
                name={s.label}
                stroke={s.color}
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
                connectNulls
              />
            ) : (
              <Area
                key={s.dataKey}
                yAxisId={s.yAxisId || 'left'}
                type="monotone"
                dataKey={s.dataKey}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                fill={`url(#grad-${s.dataKey})`}
                fillOpacity={1}
                dot={false}
                activeDot={{ r: 3 }}
                connectNulls
              />
            )
          )}
        </ComposedChart>
      </ResponsiveContainer>
  );

  if (!card) return chart;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      {title && <h3 className="mb-3 text-sm font-semibold text-gray-700">{title}</h3>}
      {chart}
    </div>
  );
}
